import type { ApolloError } from '@apollo/client';
import React from 'react';
import EncourageInstallationPlatform from './EncourageInstallationPlatform';
import ExternalBrandButton from './ExternalBrandButton';
import SearchResultCards from './SearchResultCards';
import Card from '../components/Card';
import EmptyState from '../components/EmptyState';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import IntegrationCardContent from '../integrations/IntegrationCardContent';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';

const PLATFORM_FRAGMENT = gql(`
  fragment SearchViewExternalResults_platform on ExternalSearchPlatform {
    platform
    personalAuthorized
    personalAuthUrl
    teamAuthorized
    teamAuthUrl
    ...EncourageInstallationPlatform_platform
  }
`);

const MENTION_FRAGMENT = gql(`
  fragment SearchViewExternalResults_mention on Mention {
    id
    url
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...IntegrationCardContent_mention
  }
`);

interface SearchViewExternalResultsProps {
  results: DocumentType<typeof MENTION_FRAGMENT>[];
  authError?: string;
  encouragedPlatformSet: Set<string>;
  platform: DocumentType<typeof PLATFORM_FRAGMENT>;
  loading: boolean;
  error?: ApolloError;
}

const SearchViewExternalResults = ({
  results,
  authError,
  encouragedPlatformSet,
  platform,
  loading,
  error,
}: SearchViewExternalResultsProps) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    personalAuthorized,
    personalAuthUrl,
    platform: platformKey,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    teamAuthorized,
    teamAuthUrl,
  } = platform;

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const isAuthorized = personalAuthorized || teamAuthorized;

  if (loading || !platformInfo) return null;

  const isTokenInvalid = error?.graphQLErrors.some(
    ({ message }) => message === 'TOKEN_INVALID',
  );
  const { icon, name } = platformInfo;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const encourageInstallation = encouragedPlatformSet.has(platformKey);
  const authUrl = personalAuthUrl || teamAuthUrl;

  const authButton = authUrl && (
    <ExternalBrandButton icon={icon} to={authUrl}>
      Connect {name}
    </ExternalBrandButton>
  );

  if (authError || isTokenInvalid) {
    const message =
      authError ||
      (authButton
        ? 'Please reconnect'
        : 'Please contact a team admin to reconnect');

    return (
      <EmptyState
        imageName="notFound"
        title={`Oops, we could not connect to the ${name} account`}
        message={message}
        action={authButton}
      />
    );
  }

  if (results?.length) {
    return (
      <SearchResultCards>
        {results.map((result) =>
          result.type && result.url ? (
            <Card key={result.id} to={result.url} newTab>
              <IntegrationCardContent
                url={result.url}
                mention={result}
                highlight
                previewEnabled
              />
            </Card>
          ) : null,
        )}
      </SearchResultCards>
    );
  }

  if (isAuthorized) {
    return !personalAuthorized && personalAuthUrl && icon ? (
      <EmptyState
        imageName="notFound"
        title="No results found"
        message={`Try another search with different query.
      You can also connect your own account to include private results.`}
        action={authButton}
      />
    ) : (
      <EmptyState
        imageName="notFound"
        title="No results found"
        message="Try another search with fewer keywords"
      />
    );
  }

  return encourageInstallation ? (
    <EncourageInstallationPlatform platform={platform} />
  ) : (
    <EmptyState
      imageName="notFound"
      title={`Search ${name}`}
      message={`Look no further, Slab can help you find files in ${name} too!`}
      action={authButton}
    />
  );
};

export default SearchViewExternalResults;
